import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import CreateStoryDialog from './CreateStoryDialog';
import StoryViewerDialog from './StoryViewerDialog';
import {
  getPublicStoryUrl,
  listActiveStories,
  listReactionsForStories,
  Story,
  StoryReaction,
} from '../../services/supabase/story.service';

const REACTION_EMOJI: Record<StoryReaction, string> = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😡',
};

const AUTO_SCROLL_MS = 4500;
const USER_PAUSE_MS = 12000;

export default function StoriesBar() {
  const { userProfile } = useAuth();

  const stripScrollRef = useRef<HTMLDivElement | null>(null);
  const userPauseUntilRef = useRef(0);
  const suppressScrollPauseUntilRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stories, setStories] = useState<Story[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const [myReactionByStoryId, setMyReactionByStoryId] = useState<Record<string, StoryReaction | null>>({});
  const [reactionCountsByStoryId, setReactionCountsByStoryId] = useState<
    Record<string, Record<StoryReaction, number>>
  >({});

  const [tileFloaters, setTileFloaters] = useState<Record<string, { emoji: string; leftPct: number } | null>>({});

  const storyImageUrlById = useMemo(() => {
    const map: Record<string, string> = {};
    stories.forEach((s) => {
      map[s.id] = getPublicStoryUrl(s.image_path);
    });
    return map;
  }, [stories]);

  const reactionIconsFor = useCallback(
    (storyId: string) => {
      const counts = reactionCountsByStoryId[storyId];
      if (!counts) return [];
      const entries = Object.entries(counts) as Array<[StoryReaction, number]>;
      return entries
        .filter(([, v]) => (Number(v) || 0) > 0)
        .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0))
        .slice(0, 3)
        .map(([k]) => REACTION_EMOJI[k]);
    },
    [reactionCountsByStoryId]
  );

  const computeReactions = useCallback(
    async (nextStories: Story[]) => {
      if (!userProfile?.id || nextStories.length === 0) {
        setMyReactionByStoryId({});
        setReactionCountsByStoryId({});
        return;
      }

      const ids = nextStories.map((s) => s.id);
      const rows = await listReactionsForStories(ids);

      const counts: Record<string, Record<StoryReaction, number>> = {};
      const mine: Record<string, StoryReaction | null> = {};

      rows.forEach((r) => {
        const storyId = r.story_id;
        const reaction = r.reaction as StoryReaction;
        counts[storyId] = counts[storyId] || {
          like: 0,
          love: 0,
          laugh: 0,
          wow: 0,
          sad: 0,
          angry: 0,
        };
        counts[storyId][reaction] = (counts[storyId][reaction] || 0) + 1;

        if (r.user_id === userProfile.id) {
          mine[storyId] = reaction;
        }
      });

      setReactionCountsByStoryId(counts);
      setMyReactionByStoryId(mine);
    },
    [userProfile?.id]
  );

  const bumpStripScrollPause = useCallback(() => {
    userPauseUntilRef.current = Date.now() + USER_PAUSE_MS;
  }, []);

  const handleStripScroll = useCallback(() => {
    if (Date.now() < suppressScrollPauseUntilRef.current) return;
    userPauseUntilRef.current = Date.now() + USER_PAUSE_MS;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listActiveStories();
      setStories(data);
      await computeReactions(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load stories.');
      setStories([]);
      setMyReactionByStoryId({});
      setReactionCountsByStoryId({});
    } finally {
      setLoading(false);
    }
  }, [computeReactions]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (loading || error || stories.length === 0) return;

    const el = stripScrollRef.current;
    if (!el) return;

    const tick = () => {
      if (Date.now() < userPauseUntilRef.current) return;
      const node = stripScrollRef.current;
      if (!node) return;
      if (node.scrollWidth <= node.clientWidth + 1) return;

      const first = node.firstElementChild as HTMLElement | null;
      if (!first) return;
      const gapRaw = getComputedStyle(node).columnGap || getComputedStyle(node).gap || '0';
      const gap = parseFloat(gapRaw) || 0;
      const step = first.offsetWidth + gap;

      const atEnd = node.scrollLeft + node.clientWidth >= node.scrollWidth - 2;
      suppressScrollPauseUntilRef.current = Date.now() + 750;
      if (atEnd) {
        node.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        node.scrollBy({ left: step, behavior: 'smooth' });
      }
    };

    const id = window.setInterval(tick, AUTO_SCROLL_MS);
    return () => window.clearInterval(id);
  }, [loading, error, stories.length]);

  useEffect(() => {
    // One-time floating reaction animation when stories appear/refresh.
    if (stories.length === 0) return;
    stories.forEach((s) => {
      // Only animate once per refresh for each story.
      const icons = reactionIconsFor(s.id);
      if (icons.length === 0) return;
      const emoji = icons[0];
      const leftPct = 20 + Math.random() * 60;
      setTileFloaters((prev) => ({ ...prev, [s.id]: { emoji, leftPct } }));
      window.setTimeout(() => {
        setTileFloaters((prev) => ({ ...prev, [s.id]: null }));
      }, 900);
    });
  }, [stories, reactionIconsFor]);

  useEffect(() => {
    // Auto-refresh when stories/reactions change (no manual refresh needed).
    // Note: requires Realtime to be enabled for these tables in Supabase.
    const channel = supabase
      .channel('stories-feed')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'stories' },
        () => refresh()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'story_reactions' },
        () => refresh()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refresh]);

  useEffect(() => {
    if (viewerOpen && stories.length === 0) {
      setViewerOpen(false);
    }
  }, [viewerOpen, stories.length]);

  useEffect(() => {
    if (!viewerOpen || stories.length === 0) return;
    setActiveIndex((i) => Math.min(i, stories.length - 1));
  }, [stories.length, viewerOpen]);

  const handleOpenViewer = (idx: number) => {
    setActiveIndex(idx);
    setViewerOpen(true);
  };

  const totalReactsFor = (storyId: string) => {
    const c = reactionCountsByStoryId[storyId];
    if (!c) return 0;
    return Object.values(c).reduce((s, v) => s + (Number(v) || 0), 0);
  };

  if (!userProfile?.id) return null;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.02) 100%)',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Stories
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Post quick updates that disappear in 24 hours
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Refresh stories" arrow>
              <IconButton size="small" onClick={refresh} aria-label="refresh stories">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create story" arrow>
              <IconButton
                size="small"
                onClick={() => setCreateOpen(true)}
                aria-label="create story"
                sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          ref={stripScrollRef}
          onPointerDown={bumpStripScrollPause}
          onWheel={bumpStripScrollPause}
          onScroll={handleStripScroll}
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 1.5 },
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100%',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            scrollSnapType: { xs: 'x mandatory', sm: 'none' },
            pb: 0.5,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 999 },
          }}
        >
          {/* Create tile */}
          <Box
            onClick={() => setCreateOpen(true)}
            sx={{
              flex: '0 0 auto',
              width: { xs: 112, sm: 120 },
              height: { xs: 152, sm: 160 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              transition: 'transform 0.12s ease',
              '&:hover': { transform: 'translateY(-2px)' },
              scrollSnapAlign: { xs: 'start', sm: 'none' },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.contrastText',
              }}
            >
              <AddIcon />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>Create</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, textAlign: 'center' }}>
              Add a photo
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : error ? (
            <Box sx={{ px: 1 }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          ) : stories.length === 0 ? (
            <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No stories yet. Be the first to post.
              </Typography>
            </Box>
          ) : (
            stories.map((s, idx) => (
              <Box
                key={s.id}
                onClick={() => handleOpenViewer(idx)}
                sx={{
                  flex: '0 0 auto',
                  width: { xs: 112, sm: 120 },
                  height: { xs: 152, sm: 160 },
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: myReactionByStoryId[s.id] ? 'primary.main' : 'success.main',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                  scrollSnapAlign: { xs: 'start', sm: 'none' },
                }}
              >
                <Box
                  component="img"
                  src={storyImageUrlById[s.id]}
                  alt={`${s.author_username || 'Story'} story`}
                  loading="lazy"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'saturate(1.05)',
                    transform: 'scale(1.02)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.75) 100%)',
                  }}
                />

                {/* one-time floater (no click needed) */}
                {tileFloaters[s.id] && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${tileFloaters[s.id]!.leftPct}%`,
                      bottom: 18,
                      transform: 'translateX(-50%)',
                      fontSize: 22,
                      filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.45))',
                      pointerEvents: 'none',
                      animation: 'storyTileFloaterUp 0.9s ease-out forwards',
                      '@keyframes storyTileFloaterUp': {
                        '0%': { transform: 'translate(-50%, 0) scale(0.9)', opacity: 0.0 },
                        '15%': { opacity: 1 },
                        '100%': { transform: 'translate(-50%, -120px) scale(1.15)', opacity: 0 },
                      },
                    }}
                  >
                    {tileFloaters[s.id]!.emoji}
                  </Box>
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    left: 10,
                    right: 10,
                    bottom: 10,
                    color: 'white',
                  }}
                >
                  <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', lineHeight: 1.1 }} noWrap>
                    {s.author_username || 'Unknown'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25 }}>
                    {reactionIconsFor(s.id).map((e, i) => (
                      <Box
                        key={`${s.id}_${i}`}
                        sx={{
                          fontSize: 16,
                          lineHeight: 1,
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
                        }}
                      >
                        {e}
                      </Box>
                    ))}
                    {totalReactsFor(s.id) === 0 && (
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        —
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Paper>

      <CreateStoryDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        authorId={userProfile.id}
        onCreated={refresh}
      />

      <StoryViewerDialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        stories={stories}
        activeIndex={activeIndex}
        onChangeIndex={setActiveIndex}
        userId={userProfile.id}
        myReactionByStoryId={myReactionByStoryId}
        reactionCountsByStoryId={reactionCountsByStoryId}
        onReacted={refresh}
      />
    </>
  );
}

